/**
 * Popup Manager
 *
 * Manages all popup instances on the page, initializes triggers,
 * and provides the public JavaScript API.
 */

import type { PopupConfig } from '../shared/types/popup';
import type { ShowPopupOptions } from './types';
import { Popup } from './popup';
import { ButtonTrigger } from './triggers/button-trigger';
import { TimeoutTrigger } from './triggers/timeout-trigger';
import { ExitIntentTrigger } from './triggers/exit-intent-trigger';
import { ScrollDepthTrigger } from './triggers/scroll-depth-trigger';
import { HashTrigger } from './triggers/hash-trigger';
import { PageLoadTrigger } from './triggers/page-load-trigger';
import { hideInitialPopup } from './inline-content';
import { checkTargeting } from './targeting';
import { getCookie } from './utils/cookies';
import { createLogger } from '../shared/logger';

const logger = createLogger('PopupManager');

interface TriggerInstance {
	init: () => void;
	destroy: () => void;
}

interface ManagedPopup {
	popup: Popup;
	triggers: TriggerInstance[];
	config: PopupConfig;
}

export class PopupManager {
	private popups: Map<number, ManagedPopup> = new Map();

	constructor() {
		logger.log('PopupManager constructor');
		this.setupPublicAPI();
	}

	/**
	 * Initialize all popups from config
	 */
	public init(configs: PopupConfig[]): void {
		logger.group(`Initializing ${configs.length} popup(s)`, async () => {
			for (let index = 0; index < configs.length; index++) {
				const config = configs[index];
				logger.log(
					`Creating popup ${index + 1}/${configs.length}:`,
					config
				);
				await this.createPopup(config);
			}
		});
	}

	/**
	 * Check if a popup was previously dismissed
	 */
	private wasDismissed(config: PopupConfig): boolean {
		if (!config.behavior.respectDismissal) {
			return false;
		}
		return getCookie(config.behavior.cookieName) === '1';
	}

	/**
	 * Create a popup instance with its triggers
	 */
	private async createPopup(config: PopupConfig): Promise<void> {
		logger.log(`Popup ${config.feedId} Setup`);
		logger.log('feedId:', config.feedId);
		logger.log('trigger type:', config.trigger.type);
		logger.log('isActive:', config.isActive);

		// Check if feed is inactive - skip triggers but allow manual launch via JS API
		// Inactive feeds are only included for admin testing via the admin bar
		if (!config.isActive) {
			hideInitialPopup(config.feedId);
			logger.log(
				'Feed is inactive, skipping trigger initialization (admin testing only)'
			);
			// Still create the popup for JS API/admin bar force-launch
			const popup = new Popup(config);
			this.popups.set(config.feedId, { popup, triggers: [], config });
			return;
		}

		// Check if popup was dismissed - skip creating triggers entirely
		if (this.wasDismissed(config)) {
			hideInitialPopup(config.feedId);
			logger.log(
				'Popup was previously dismissed, skipping trigger initialization'
			);
			// Still create the popup for JS API access, but don't initialize triggers
			const popup = new Popup(config);
			this.popups.set(config.feedId, { popup, triggers: [], config });
			return;
		}

		// Check targeting conditions.
		// Server-side conditions have `serverResult` attached by PHP, which the
		// condition-registry uses automatically. Client-side conditions are evaluated
		// in JS. Non-cacheable server conditions (user_role) are verified via AJAX.
		const targetingGroups = config.targeting?.groups ?? [];
		if (targetingGroups.length > 0) {
			const result = await checkTargeting(
				targetingGroups,
				config.ajaxUrl,
				config.feedId
			);
			if (!result.shouldShow) {
				hideInitialPopup(config.feedId);
				logger.log(
					`Popup ${config.feedId} targeting not matched: ${result.reason}`
				);
				// Still create the popup for JS API force-launch, but don't initialize triggers
				const popup = new Popup(config);
				this.popups.set(config.feedId, { popup, triggers: [], config });
				return;
			}
		}

		const popup = new Popup(config);

		// Create and initialize triggers
		const triggers = this.createTriggersForPopup(popup, config);

		// Store the managed popup
		this.popups.set(config.feedId, { popup, triggers, config });
		logger.log('Popup stored, total popups:', this.popups.size);
	}

	/**
	 * Create and initialize triggers for a popup based on its config.
	 * Returns the array of initialized trigger instances.
	 */
	private createTriggersForPopup(
		popup: Popup,
		config: PopupConfig
	): TriggerInstance[] {
		const triggers: TriggerInstance[] = [];

		// Create appropriate trigger based on type
		switch (config.trigger.type) {
			case 'page_load':
				triggers.push(new PageLoadTrigger(popup));
				break;

			case 'button':
				logger.log(
					'Creating ButtonTrigger with selector:',
					config.trigger.selector
				);
				triggers.push(
					new ButtonTrigger(popup, config.trigger.selector)
				);
				break;

			case 'timeout': {
				// Convert delay to milliseconds based on unit
				const delayMs =
					config.trigger.delayUnit === 's'
						? config.trigger.delay * 1000
						: config.trigger.delay;
				logger.log(
					'Creating TimeoutTrigger with delay:',
					config.trigger.delay,
					config.trigger.delayUnit,
					'->',
					delayMs,
					'ms'
				);
				triggers.push(new TimeoutTrigger(popup, delayMs));
				break;
			}

			case 'exit_intent':
				logger.log(
					'Creating ExitIntentTrigger with sensitivity:',
					config.trigger.sensitivity
				);
				triggers.push(
					new ExitIntentTrigger(popup, config.trigger.sensitivity)
				);
				break;

			case 'scroll_depth':
				logger.log(
					'Creating ScrollDepthTrigger with value:',
					config.trigger.scrollDepthValue,
					config.trigger.scrollDepthUnit
				);
				triggers.push(
					new ScrollDepthTrigger(
						popup,
						config.trigger.scrollDepthValue,
						config.trigger.scrollDepthUnit
					)
				);
				break;

			case 'hash':
				logger.log(
					'Creating HashTrigger with hash:',
					config.trigger.hashValue
				);
				triggers.push(new HashTrigger(popup, config.trigger.hashValue));
				break;

			case 'js_api':
				logger.log('js_api trigger - no automatic trigger');
				// No automatic trigger - controlled via JavaScript API
				break;
		}

		logger.log(`Created ${triggers.length} trigger(s), initializing...`);

		// Initialize all triggers
		triggers.forEach((trigger, i) => {
			logger.log(`Initializing trigger ${i + 1}/${triggers.length}`);
			trigger.init();
		});

		return triggers;
	}

	/**
	 * Show a popup by feed ID
	 */
	public show(feedId: number, options?: ShowPopupOptions): void {
		const managed = this.popups.get(feedId);
		if (managed) {
			managed.popup.show();
		} else if (options?.bypassDismissal) {
			// Popup might not exist because it was dismissed - create it on demand
			this.forceLaunch(feedId);
		}
	}

	/**
	 * Force launch a popup, bypassing dismissal cookies
	 * Creates the popup on demand if it doesn't exist
	 */
	public forceLaunch(feedId: number): void {
		logger.log(`Force launching popup ${feedId}`);

		// Check if popup already exists
		const existing = this.popups.get(feedId);
		if (existing) {
			// Use forceShow to bypass dismissal cookie check
			existing.popup.forceShow();
			return;
		}

		// Find the config for this feed
		const config = window.gpPopupsConfig?.find((c) => c.feedId === feedId);
		if (!config) {
			logger.warn(
				`Cannot force launch popup ${feedId}: config not found`
			);
			return;
		}

		// Create popup without triggers (force launch is manual)
		const popup = new Popup(config);
		this.popups.set(feedId, { popup, triggers: [], config });

		// Show immediately, bypassing dismissal cookie check
		popup.forceShow();
	}

	/**
	 * Hide a popup by feed ID
	 */
	public hide(feedId: number): void {
		const managed = this.popups.get(feedId);
		if (managed) {
			managed.popup.hide();
		}
	}

	/**
	 * Toggle a popup by feed ID
	 */
	public toggle(feedId: number): void {
		const managed = this.popups.get(feedId);
		if (managed) {
			managed.popup.toggle();
		}
	}

	/**
	 * Check if a popup is open
	 */
	public isOpen(feedId: number): boolean {
		const managed = this.popups.get(feedId);
		return managed ? managed.popup.getIsOpen() : false;
	}

	/**
	 * Destroy all popups
	 */
	public destroy(): void {
		this.popups.forEach(({ popup, triggers }) => {
			triggers.forEach((trigger) => trigger.destroy());
			popup.destroy();
		});
		this.popups.clear();
	}

	/**
	 * Test trigger for an inactive feed.
	 * This initializes the triggers for a feed that was skipped due to being inactive,
	 * allowing admins to test the trigger behavior before activating the feed.
	 */
	public testTrigger(feedId: number): void {
		logger.log(`Testing trigger for feed ${feedId}`);

		const managed = this.popups.get(feedId);
		if (!managed) {
			logger.warn(`Cannot test trigger: popup ${feedId} not found`);
			return;
		}

		// If triggers are already initialized, don't re-initialize
		if (managed.triggers.length > 0) {
			logger.log(`Triggers already initialized for feed ${feedId}`);
			return;
		}

		// For js_api triggers, just launch the popup immediately
		if (managed.config.trigger.type === 'js_api') {
			logger.log('js_api trigger - launching immediately');
			managed.popup.forceShow();
			return;
		}

		// Create and initialize triggers using the shared method
		const triggers = this.createTriggersForPopup(
			managed.popup,
			managed.config
		);

		// Update the managed popup with the new triggers
		managed.triggers = triggers;
	}

	/**
	 * Set up the public JavaScript API on window.GPPopups
	 */
	private setupPublicAPI(): void {
		window.GPPopups = {
			show: (feedId: number, options?: ShowPopupOptions) =>
				this.show(feedId, options),
			hide: (feedId: number) => this.hide(feedId),
			toggle: (feedId: number) => this.toggle(feedId),
			isOpen: (feedId: number) => this.isOpen(feedId),
			forceLaunch: (feedId: number) => this.forceLaunch(feedId),
			testTrigger: (feedId: number) => this.testTrigger(feedId),
		};
	}
}
